import { useState, useCallback, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { createClient } from '@supabase/supabase-js';
import { 
  Database, 
  Upload, 
  Download,
  Server,
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Cloud,
  RefreshCw,
  Loader2,
  ArrowRight,
  Shield,
  Key,
  Link2,
  Copy,
  Eye,
  EyeOff,
  FileJson,
  Table2,
  Image,
  Users,
  Package,
  Settings,
  Zap,
  Archive,
  FolderArchive,
  HardDrive,
  Lock,
  RefreshCcw
} from "lucide-react";

// Tables prioritaires avec leurs dépendances (pour l'ordre de migration)
const PRIORITY_TABLES: Record<string, { label: string; priority: number; dependencies: string[] }> = {
  'profiles': { label: 'Utilisateurs', priority: 1, dependencies: [] },
  'categories': { label: 'Catégories', priority: 2, dependencies: [] },
  'relays': { label: 'Points Relais', priority: 2, dependencies: ['profiles'] },
  'products': { label: 'Produits', priority: 3, dependencies: ['profiles', 'categories'] },
  'orders': { label: 'Commandes', priority: 4, dependencies: ['profiles', 'products', 'relays'] },
  'order_items': { label: 'Articles Commandes', priority: 5, dependencies: ['orders', 'products'] },
  'vendor_applications': { label: 'Candidatures Vendeurs', priority: 3, dependencies: ['profiles'] },
  'vendor_transactions': { label: 'Transactions Vendeurs', priority: 5, dependencies: ['profiles', 'orders'] },
  'vendor_balances': { label: 'Soldes Vendeurs', priority: 6, dependencies: ['profiles'] },
  'payments': { label: 'Paiements', priority: 5, dependencies: ['orders'] },
  'pickup_tokens': { label: 'Tokens Retrait', priority: 6, dependencies: ['orders'] },
  'translations': { label: 'Traductions', priority: 1, dependencies: [] },
  'site_settings': { label: 'Paramètres Site', priority: 1, dependencies: [] },
};

interface TableInfo {
  name: string;
  label: string;
  priority: number;
  dependencies: string[];
  rowCount?: number;
}

// Buckets Storage
const STORAGE_BUCKETS = [
  { name: 'avatars', label: 'Avatars Utilisateurs' },
  { name: 'products', label: 'Images Produits' },
  { name: 'vendor-documents', label: 'Documents Vendeurs' },
  { name: 'site-assets', label: 'Assets Site' },
  { name: 'category-images', label: 'Images Catégories' },
  { name: 'relay-images', label: 'Images Points Relais' },
];

// Edge Functions connues
const EDGE_FUNCTIONS = [
  'send-admin-vendor-application',
  'send-super-admin-notification',
  'send-vendor-confirmation',
  'send-vendor-decision',
  'send-order-confirmation',
  'send-newsletter-welcome',
  'send-newsletter',
  'send-password-reset',
  'generate-vendor-invoice',
  'process-payment',
  'regenerate-qr-code',
  'create-stripe-session',
  'stripe-webhook',
];

// Secrets connus (noms seulement, pas les valeurs)
const KNOWN_SECRETS = [
  'STRIPE_SECRET_KEY',
  'STRIPE_WEBHOOK_SECRET',
  'RESEND_API_KEY',
  'SUPABASE_SERVICE_ROLE_KEY',
  'JWT_SECRET',
  'OPENAI_API_KEY',
];

interface BackupData {
  timestamp: string;
  tables: Record<string, any[]>;
  edgeFunctions: string[];
  secrets: string[];
  storageFiles: Record<string, string[]>;
  metadata: {
    projectId: string;
    totalTables: number;
    totalRows: number;
    version: string;
  };
}

interface MigrationStep {
  id: string;
  name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'skipped';
  progress: number;
  message?: string;
  rowsProcessed?: number;
  totalRows?: number;
}

interface ConnectionStatus {
  connected: boolean;
  projectId?: string;
  version?: string;
  error?: string;
}

export function SupabaseMigrationManager() {
  const { toast } = useToast();
  
  // Tables state - dynamically loaded
  const [allTables, setAllTables] = useState<TableInfo[]>([]);
  const [isLoadingTables, setIsLoadingTables] = useState(true);
  
  // Connection state
  const [targetUrl, setTargetUrl] = useState('');
  const [targetAnonKey, setTargetAnonKey] = useState('');
  const [targetServiceKey, setTargetServiceKey] = useState('');
  const [showServiceKey, setShowServiceKey] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus | null>(null);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  
  // Migration state
  const [selectedTables, setSelectedTables] = useState<string[]>([]);
  const [selectedBuckets, setSelectedBuckets] = useState<string[]>([]);
  const [migrateAuth, setMigrateAuth] = useState(false);
  const [migrateStorage, setMigrateStorage] = useState(false);
  const [migrationSteps, setMigrationSteps] = useState<MigrationStep[]>([]);
  const [isMigrating, setIsMigrating] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState(0);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // Backup state
  const [autoBackupEnabled, setAutoBackupEnabled] = useState(true);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [backupProgress, setBackupProgress] = useState(0);
  const [lastBackup, setLastBackup] = useState<BackupData | null>(null);
  const [backupCompleted, setBackupCompleted] = useState(false);
  
  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  // Load all tables from Supabase on mount
  const loadTables = useCallback(async () => {
    setIsLoadingTables(true);
    try {
      // Try RPC function first (if exists)
      const { data: rpcData, error: rpcError } = await supabase.rpc('get_all_tables' as any);
      
      if (!rpcError && rpcData && Array.isArray(rpcData)) {
        // Use RPC result
        const tables: TableInfo[] = rpcData.map((t: any) => {
          const name = t.table_name;
          const priority = PRIORITY_TABLES[name]?.priority || 5;
          const label = PRIORITY_TABLES[name]?.label || name.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
          const dependencies = PRIORITY_TABLES[name]?.dependencies || [];
          const rowCount = t.row_count;
          return { name, label, priority, dependencies, rowCount };
        });
        
        setAllTables(tables.sort((a, b) => a.priority - b.priority || a.name.localeCompare(b.name)));
        setSelectedTables(tables.map(t => t.name));
      } else {
        // Fallback: Use a hardcoded comprehensive list based on project structure
        const allKnownTables = [
          'profiles', 'categories', 'products', 'orders', 'order_items',
          'relays', 'vendor_applications', 'vendor_transactions', 'vendor_balances',
          'vendor_withdrawal_requests', 'payments', 'pickup_tokens', 'carts', 'cart_items',
          'favorites', 'order_disputes', 'translations', 'site_settings', 'cms_content',
          'notifications', 'newsletter_subscribers', 'audit_logs', 'promo_codes',
          'promo_code_usage', 'product_promotions', 'advertisements', 'ad_impressions',
          'ad_clicks', 'footer_sections', 'footer_links', 'delivery_zones', 'employee_profiles',
          'employee_documents', 'leave_requests', 'evaluations', 'trainings', 'payroll',
          'vendor_invoices', 'api_keys', 'cron_jobs', 'cron_job_logs', 'fail2ban_events',
          'storage_backups', 'currency_rates', 'reviews', 'messages', 'conversations',
          'product_variants', 'product_images', 'category_translations', 'legal_pages'
        ];

        // Check which tables actually exist by trying to query them
        const existingTables: TableInfo[] = [];
        
        for (const tableName of allKnownTables) {
          try {
            const { error } = await supabase
              .from(tableName as any)
              .select('*', { count: 'exact', head: true });
            
            if (!error) {
              const priority = PRIORITY_TABLES[tableName]?.priority || 5;
              const label = PRIORITY_TABLES[tableName]?.label || tableName.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase());
              const dependencies = PRIORITY_TABLES[tableName]?.dependencies || [];
              existingTables.push({ name: tableName, label, priority, dependencies });
            }
          } catch (e) {
            // Table doesn't exist, skip
          }
        }
        
        setAllTables(existingTables.sort((a, b) => a.priority - b.priority || a.name.localeCompare(b.name)));
        setSelectedTables(existingTables.map(t => t.name));
      }
    } catch (error: any) {
      console.error('Error loading tables:', error);
      // Fallback to known tables list
      const knownTables = Object.keys(PRIORITY_TABLES);
      const tables: TableInfo[] = knownTables.map(name => ({
        name,
        label: PRIORITY_TABLES[name].label,
        priority: PRIORITY_TABLES[name].priority,
        dependencies: PRIORITY_TABLES[name].dependencies,
      }));
      setAllTables(tables);
      setSelectedTables(tables.map(t => t.name));
      
      toast({
        variant: "destructive",
        title: "Erreur de chargement",
        description: "Impossible de charger la liste des tables. Liste par défaut utilisée.",
      });
    } finally {
      setIsLoadingTables(false);
    }
  }, [toast]);

  useEffect(() => {
    loadTables();
  }, [loadTables]);

  // Test connection to target Supabase
  const testConnection = async () => {
    if (!targetUrl || !targetAnonKey) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Veuillez renseigner l'URL et la clé anon du projet cible",
      });
      return;
    }

    setIsTestingConnection(true);
    setConnectionStatus(null);

    try {
      const targetClient = createClient(targetUrl, targetAnonKey);
      
      // Test basic connection
      const { data, error } = await targetClient.from('profiles').select('count', { count: 'exact', head: true });
      
      if (error && !error.message.includes('does not exist')) {
        throw error;
      }

      // Extract project ID from URL
      const projectId = targetUrl.match(/https:\/\/([^.]+)\.supabase\.co/)?.[1] || 'unknown';

      setConnectionStatus({
        connected: true,
        projectId,
        version: 'Supabase v2',
      });

      toast({
        title: "Connexion réussie",
        description: `Connecté au projet ${projectId}`,
      });

    } catch (error: any) {
      setConnectionStatus({
        connected: false,
        error: error.message || "Impossible de se connecter",
      });

      toast({
        variant: "destructive",
        title: "Échec de connexion",
        description: error.message || "Vérifiez vos identifiants",
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  // Perform automatic backup before migration
  const performAutoBackup = async (): Promise<BackupData | null> => {
    setIsBackingUp(true);
    setBackupProgress(0);
    setBackupCompleted(false);

    try {
      const backupData: BackupData = {
        timestamp: new Date().toISOString(),
        tables: {},
        edgeFunctions: EDGE_FUNCTIONS,
        secrets: KNOWN_SECRETS,
        storageFiles: {},
        metadata: {
          projectId: 'apmnhnunfrcbydwysfez',
          totalTables: 0,
          totalRows: 0,
          version: '1.0',
        },
      };

      const tablesToBackup = selectedTables;
      const totalSteps = tablesToBackup.length + (migrateStorage ? selectedBuckets.length : 0);
      let currentStep = 0;

      // Backup tables
      for (const tableName of tablesToBackup) {
        try {
          const { data, error } = await supabase
            .from(tableName as any)
            .select('*');

          if (!error && data) {
            backupData.tables[tableName] = data;
            backupData.metadata.totalRows += data.length;
          } else {
            backupData.tables[tableName] = [];
            console.warn(`Backup ${tableName}:`, error?.message);
          }
        } catch (e) {
          backupData.tables[tableName] = [];
        }

        currentStep++;
        setBackupProgress(Math.round((currentStep / totalSteps) * 100));
      }

      backupData.metadata.totalTables = Object.keys(backupData.tables).length;

      // Backup storage file list
      if (migrateStorage && selectedBuckets.length > 0) {
        for (const bucket of selectedBuckets) {
          try {
            const { data: files, error } = await supabase.storage
              .from(bucket)
              .list('', { limit: 1000 });

            if (!error && files) {
              backupData.storageFiles[bucket] = files.map(f => f.name);
            }
          } catch (e) {
            backupData.storageFiles[bucket] = [];
          }

          currentStep++;
          setBackupProgress(Math.round((currentStep / totalSteps) * 100));
        }
      }

      // Create and download backup file
      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nazandoo-backup-pre-migration-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setLastBackup(backupData);
      setBackupCompleted(true);

      toast({
        title: "Sauvegarde terminée",
        description: `${backupData.metadata.totalTables} tables, ${backupData.metadata.totalRows} lignes sauvegardées`,
      });

      return backupData;

    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur de sauvegarde",
        description: error.message,
      });
      return null;
    } finally {
      setIsBackingUp(false);
      setBackupProgress(100);
    }
  };

  // Export data as JSON
  const exportData = async () => {
    setIsExporting(true);
    setExportProgress(0);

    try {
      const exportData: Record<string, any[]> = {};
      const tablesToExport = selectedTables;
      
      for (let i = 0; i < tablesToExport.length; i++) {
        const tableName = tablesToExport[i];
        setExportProgress(Math.round((i / tablesToExport.length) * 100));

        const { data, error } = await supabase
          .from(tableName as any)
          .select('*');

        if (error) {
          console.warn(`Erreur export ${tableName}:`, error.message);
          exportData[tableName] = [];
        } else {
          exportData[tableName] = data || [];
        }
      }

      // Create and download JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `nazandoo-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Export terminé",
        description: `${tablesToExport.length} tables exportées`,
      });

    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur d'export",
        description: error.message,
      });
    } finally {
      setIsExporting(false);
      setExportProgress(100);
    }
  };

  // Start migration
  const startMigration = async () => {
    if (!connectionStatus?.connected || !targetServiceKey) {
      toast({
        variant: "destructive",
        title: "Erreur",
        description: "Connexion non établie ou clé service manquante",
      });
      return;
    }

    setShowConfirmDialog(false);
    
    // Perform automatic backup if enabled
    if (autoBackupEnabled) {
      toast({
        title: "Sauvegarde automatique",
        description: "Création d'une sauvegarde avant migration...",
      });
      
      const backup = await performAutoBackup();
      
      if (!backup) {
        toast({
          variant: "destructive",
          title: "Migration annulée",
          description: "La sauvegarde automatique a échoué. Migration annulée par sécurité.",
        });
        return;
      }
    }
    
    setIsMigrating(true);
    setMigrationProgress(0);

    // Sort tables by priority and dependencies
    const sortedTables = [...selectedTables]
      .map(name => allTables.find(t => t.name === name) || { name, label: name, priority: 5, dependencies: [] })
      .sort((a, b) => a.priority - b.priority);

    // Initialize steps
    const steps: MigrationStep[] = sortedTables.map(table => ({
      id: table.name,
      name: table.label,
      status: 'pending',
      progress: 0,
    }));

    if (migrateStorage && selectedBuckets.length > 0) {
      selectedBuckets.forEach(bucket => {
        steps.push({
          id: `storage_${bucket}`,
          name: `Storage: ${bucket}`,
          status: 'pending',
          progress: 0,
        });
      });
    }

    setMigrationSteps(steps);

    try {
      const targetClient = createClient(targetUrl, targetServiceKey, {
        auth: { persistSession: false }
      });

      // Migrate each table
      for (let i = 0; i < sortedTables.length; i++) {
        const table = sortedTables[i];
        const stepIndex = i;

        // Update step status
        setMigrationSteps(prev => prev.map((s, idx) => 
          idx === stepIndex ? { ...s, status: 'in_progress' } : s
        ));

        try {
          // Fetch data from source
          const { data: sourceData, error: fetchError } = await supabase
            .from(table.name as any)
            .select('*');

          if (fetchError) throw fetchError;

          const totalRows = sourceData?.length || 0;

          if (totalRows === 0) {
            setMigrationSteps(prev => prev.map((s, idx) => 
              idx === stepIndex ? { 
                ...s, 
                status: 'completed', 
                progress: 100,
                message: 'Aucune donnée',
                rowsProcessed: 0,
                totalRows: 0
              } : s
            ));
            continue;
          }

          // Insert in batches
          const batchSize = 100;
          let processed = 0;

          for (let j = 0; j < sourceData!.length; j += batchSize) {
            const batch = sourceData!.slice(j, j + batchSize);
            
            const { error: insertError } = await targetClient
              .from(table.name)
              .upsert(batch, { onConflict: 'id' });

            if (insertError) {
              console.warn(`Erreur batch ${table.name}:`, insertError.message);
            }

            processed += batch.length;
            const progress = Math.round((processed / totalRows) * 100);

            setMigrationSteps(prev => prev.map((s, idx) => 
              idx === stepIndex ? { 
                ...s, 
                progress,
                rowsProcessed: processed,
                totalRows
              } : s
            ));
          }

          setMigrationSteps(prev => prev.map((s, idx) => 
            idx === stepIndex ? { 
              ...s, 
              status: 'completed', 
              progress: 100,
              message: `${totalRows} lignes migrées`,
              rowsProcessed: totalRows,
              totalRows
            } : s
          ));

        } catch (error: any) {
          setMigrationSteps(prev => prev.map((s, idx) => 
            idx === stepIndex ? { 
              ...s, 
              status: 'failed', 
              message: error.message 
            } : s
          ));
        }

        setMigrationProgress(Math.round(((i + 1) / steps.length) * 100));
      }

      // Migrate storage if selected
      if (migrateStorage && selectedBuckets.length > 0) {
        for (let i = 0; i < selectedBuckets.length; i++) {
          const bucket = selectedBuckets[i];
          const stepIndex = sortedTables.length + i;

          setMigrationSteps(prev => prev.map((s, idx) => 
            idx === stepIndex ? { ...s, status: 'in_progress' } : s
          ));

          try {
            // List files in source bucket
            const { data: files, error: listError } = await supabase.storage
              .from(bucket)
              .list('', { limit: 1000 });

            if (listError) throw listError;

            const totalFiles = files?.length || 0;
            let processed = 0;

            for (const file of files || []) {
              // Download from source
              const { data: fileData, error: downloadError } = await supabase.storage
                .from(bucket)
                .download(file.name);

              if (downloadError) {
                console.warn(`Erreur download ${file.name}:`, downloadError.message);
                continue;
              }

              // Upload to target
              const { error: uploadError } = await targetClient.storage
                .from(bucket)
                .upload(file.name, fileData, { upsert: true });

              if (uploadError) {
                console.warn(`Erreur upload ${file.name}:`, uploadError.message);
              }

              processed++;
              const progress = Math.round((processed / totalFiles) * 100);

              setMigrationSteps(prev => prev.map((s, idx) => 
                idx === stepIndex ? { 
                  ...s, 
                  progress,
                  rowsProcessed: processed,
                  totalRows: totalFiles
                } : s
              ));
            }

            setMigrationSteps(prev => prev.map((s, idx) => 
              idx === stepIndex ? { 
                ...s, 
                status: 'completed', 
                progress: 100,
                message: `${totalFiles} fichiers migrés`
              } : s
            ));

          } catch (error: any) {
            setMigrationSteps(prev => prev.map((s, idx) => 
              idx === stepIndex ? { 
                ...s, 
                status: 'failed', 
                message: error.message 
              } : s
            ));
          }
        }
      }

      toast({
        title: "Migration terminée",
        description: "Vérifiez les résultats ci-dessous",
      });

    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erreur de migration",
        description: error.message,
      });
    } finally {
      setIsMigrating(false);
      setMigrationProgress(100);
    }
  };

  // Toggle table selection
  const toggleTable = (tableName: string) => {
    setSelectedTables(prev => 
      prev.includes(tableName) 
        ? prev.filter(t => t !== tableName)
        : [...prev, tableName]
    );
  };

  // Toggle bucket selection
  const toggleBucket = (bucketName: string) => {
    setSelectedBuckets(prev => 
      prev.includes(bucketName) 
        ? prev.filter(b => b !== bucketName)
        : [...prev, bucketName]
    );
  };

  // Copy to clipboard
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copié",
      description: "Texte copié dans le presse-papier",
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <Server className="h-6 w-6" />
            Migration Supabase
          </h2>
          <p className="text-muted-foreground">
            Migrer les données vers une nouvelle instance Supabase
          </p>
        </div>
      </div>

      {/* Warning */}
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Attention</AlertTitle>
        <AlertDescription>
          La migration de données est une opération critique. Assurez-vous d'avoir une sauvegarde 
          complète avant de procéder. Les données existantes sur l'instance cible peuvent être écrasées.
        </AlertDescription>
      </Alert>

      <Tabs defaultValue="connection" className="space-y-4">
        <TabsList>
          <TabsTrigger value="connection">
            <Link2 className="h-4 w-4 mr-2" />
            Connexion
          </TabsTrigger>
          <TabsTrigger value="tables">
            <Table2 className="h-4 w-4 mr-2" />
            Tables
          </TabsTrigger>
          <TabsTrigger value="storage">
            <Image className="h-4 w-4 mr-2" />
            Storage
          </TabsTrigger>
          <TabsTrigger value="migrate">
            <Zap className="h-4 w-4 mr-2" />
            Migration
          </TabsTrigger>
        </TabsList>

        {/* Connection Tab */}
        <TabsContent value="connection" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Configuration de l'instance cible</CardTitle>
              <CardDescription>
                Renseignez les informations de connexion à votre nouvelle instance Supabase
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="targetUrl">URL du projet Supabase</Label>
                <div className="flex gap-2">
                  <Input
                    id="targetUrl"
                    placeholder="https://votre-projet.supabase.co"
                    value={targetUrl}
                    onChange={(e) => setTargetUrl(e.target.value)}
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => copyToClipboard(targetUrl)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Disponible dans Settings → API → Project URL
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="anonKey">Clé Anon (publique)</Label>
                <div className="flex gap-2">
                  <Input
                    id="anonKey"
                    placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                    value={targetAnonKey}
                    onChange={(e) => setTargetAnonKey(e.target.value)}
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => copyToClipboard(targetAnonKey)}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Disponible dans Settings → API → anon public
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="serviceKey">Clé Service Role (secrète)</Label>
                <div className="flex gap-2">
                  <Input
                    id="serviceKey"
                    type={showServiceKey ? "text" : "password"}
                    placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                    value={targetServiceKey}
                    onChange={(e) => setTargetServiceKey(e.target.value)}
                  />
                  <Button 
                    variant="outline" 
                    size="icon"
                    onClick={() => setShowServiceKey(!showServiceKey)}
                  >
                    {showServiceKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  ⚠️ Requise pour l'écriture de données. Settings → API → service_role secret
                </p>
              </div>

              <Separator />

              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Tester la connexion</Label>
                  <p className="text-xs text-muted-foreground">
                    Vérifie que les identifiants sont valides
                  </p>
                </div>
                <Button 
                  onClick={testConnection}
                  disabled={isTestingConnection || !targetUrl || !targetAnonKey}
                >
                  {isTestingConnection ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Test en cours...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Tester
                    </>
                  )}
                </Button>
              </div>

              {connectionStatus && (
                <Alert variant={connectionStatus.connected ? "default" : "destructive"}>
                  {connectionStatus.connected ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <XCircle className="h-4 w-4" />
                  )}
                  <AlertTitle>
                    {connectionStatus.connected ? "Connecté" : "Échec de connexion"}
                  </AlertTitle>
                  <AlertDescription>
                    {connectionStatus.connected ? (
                      <>Projet: <strong>{connectionStatus.projectId}</strong></>
                    ) : (
                      connectionStatus.error
                    )}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Current instance info */}
          <Card>
            <CardHeader>
              <CardTitle>Instance source (actuelle)</CardTitle>
              <CardDescription>
                Données qui seront migrées depuis cette instance
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <Badge variant="outline" className="px-3 py-1">
                  <Cloud className="h-4 w-4 mr-2" />
                  apmnhnunfrcbydwysfez.supabase.co
                </Badge>
                <Badge variant="secondary">
                  Production
                </Badge>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tables Tab */}
        <TabsContent value="tables" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Sélection des tables</span>
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={loadTables}
                  disabled={isLoadingTables}
                >
                  <RefreshCcw className={`h-4 w-4 ${isLoadingTables ? 'animate-spin' : ''}`} />
                </Button>
              </CardTitle>
              <CardDescription>
                Choisissez les tables à migrer vers l'instance cible
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingTables ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mr-2" />
                  <span>Chargement des tables...</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <div className="space-x-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedTables(allTables.map(t => t.name))}
                      >
                        Tout sélectionner
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedTables([])}
                      >
                        Tout désélectionner
                      </Button>
                    </div>
                    <Badge variant="secondary">
                      {selectedTables.length} / {allTables.length} tables
                    </Badge>
                  </div>

                  <ScrollArea className="h-[400px] pr-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {allTables.map((table) => {
                        const isSelected = selectedTables.includes(table.name);
                        
                        return (
                          <div
                            key={table.name}
                            className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                              isSelected 
                                ? 'bg-primary/10 border-primary' 
                                : 'hover:bg-muted'
                            }`}
                            onClick={() => toggleTable(table.name)}
                          >
                            <Checkbox checked={isSelected} />
                            <Table2 className="h-4 w-4 text-muted-foreground" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">{table.label}</p>
                              <p className="text-xs text-muted-foreground truncate">{table.name}</p>
                            </div>
                            <div className="flex items-center gap-1">
                              {table.rowCount !== undefined && (
                                <Badge variant="outline" className="text-xs">
                                  {table.rowCount.toLocaleString()}
                                </Badge>
                              )}
                              <Badge variant="secondary" className="text-xs">
                                P{table.priority}
                              </Badge>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </ScrollArea>
                </>
              )}
            </CardContent>
          </Card>

          {/* Export JSON */}
          <Card>
            <CardHeader>
              <CardTitle>Export JSON</CardTitle>
              <CardDescription>
                Téléchargez les données sélectionnées au format JSON
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    Exportez les données pour une sauvegarde locale ou une migration manuelle
                  </p>
                </div>
                <Button 
                  onClick={exportData}
                  disabled={isExporting || selectedTables.length === 0}
                >
                  {isExporting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Export {exportProgress}%
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Exporter JSON
                    </>
                  )}
                </Button>
              </div>
              {isExporting && (
                <Progress value={exportProgress} className="mt-4" />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Storage Tab */}
        <TabsContent value="storage" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Migration du Storage</CardTitle>
              <CardDescription>
                Migrer les fichiers stockés dans Supabase Storage
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center space-x-2">
                <Switch
                  checked={migrateStorage}
                  onCheckedChange={setMigrateStorage}
                />
                <Label>Inclure le Storage dans la migration</Label>
              </div>

              {migrateStorage && (
                <>
                  <Separator />
                  <div className="space-y-3">
                    {STORAGE_BUCKETS.map((bucket) => (
                      <div
                        key={bucket.name}
                        className={`flex items-center space-x-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          selectedBuckets.includes(bucket.name)
                            ? 'bg-primary/10 border-primary' 
                            : 'hover:bg-muted'
                        }`}
                        onClick={() => toggleBucket(bucket.name)}
                      >
                        <Checkbox checked={selectedBuckets.includes(bucket.name)} />
                        <Image className="h-4 w-4 text-muted-foreground" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{bucket.label}</p>
                          <p className="text-xs text-muted-foreground">{bucket.name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  La migration du Storage peut prendre du temps selon le volume de fichiers.
                  Les fichiers volumineux peuvent échouer en raison des limites de timeout.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Migration Tab */}
        <TabsContent value="migrate" className="space-y-4">
          {/* Automatic Backup Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FolderArchive className="h-5 w-5" />
                Sauvegarde automatique
              </CardTitle>
              <CardDescription>
                Créer une sauvegarde complète avant la migration
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label>Sauvegarde avant migration</Label>
                  <p className="text-xs text-muted-foreground">
                    Fortement recommandé - Télécharge automatiquement une sauvegarde JSON
                  </p>
                </div>
                <Switch
                  checked={autoBackupEnabled}
                  onCheckedChange={setAutoBackupEnabled}
                />
              </div>

              <Separator />

              <div className="space-y-3">
                <Label className="text-sm font-medium">Éléments sauvegardés</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="flex items-center gap-2 p-2 rounded border bg-muted/50">
                    <Table2 className="h-4 w-4 text-blue-500" />
                    <span className="text-sm">Tables ({selectedTables.length})</span>
                    <CheckCircle2 className="h-4 w-4 text-green-500 ml-auto" />
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded border bg-muted/50">
                    <Zap className="h-4 w-4 text-purple-500" />
                    <span className="text-sm">Edge Functions ({EDGE_FUNCTIONS.length})</span>
                    <CheckCircle2 className="h-4 w-4 text-green-500 ml-auto" />
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded border bg-muted/50">
                    <Lock className="h-4 w-4 text-orange-500" />
                    <span className="text-sm">Secrets ({KNOWN_SECRETS.length} noms)</span>
                    <CheckCircle2 className="h-4 w-4 text-green-500 ml-auto" />
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded border bg-muted/50">
                    <HardDrive className="h-4 w-4 text-green-500" />
                    <span className="text-sm">Storage ({migrateStorage ? selectedBuckets.length : 0} buckets)</span>
                    {migrateStorage && selectedBuckets.length > 0 ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500 ml-auto" />
                    ) : (
                      <AlertCircle className="h-4 w-4 text-yellow-500 ml-auto" />
                    )}
                  </div>
                </div>
              </div>

              {isBackingUp && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span>Sauvegarde en cours...</span>
                    <span>{backupProgress}%</span>
                  </div>
                  <Progress value={backupProgress} />
                </div>
              )}

              {backupCompleted && lastBackup && (
                <Alert>
                  <CheckCircle2 className="h-4 w-4" />
                  <AlertTitle>Sauvegarde effectuée</AlertTitle>
                  <AlertDescription>
                    {lastBackup.metadata.totalTables} tables, {lastBackup.metadata.totalRows} lignes 
                    sauvegardées à {new Date(lastBackup.timestamp).toLocaleTimeString('fr-FR')}
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={performAutoBackup}
                  disabled={isBackingUp || isMigrating}
                  className="flex-1"
                >
                  {isBackingUp ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sauvegarde...
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4 mr-2" />
                      Sauvegarder maintenant
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Lancer la migration</CardTitle>
              <CardDescription>
                Transférer les données vers l'instance cible
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Pre-flight checks */}
              <div className="space-y-2">
                <Label>Vérifications pré-migration</Label>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {connectionStatus?.connected ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    <span className="text-sm">Connexion à l'instance cible</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {targetServiceKey ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    <span className="text-sm">Clé Service Role configurée</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedTables.length > 0 ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <XCircle className="h-4 w-4 text-red-500" />
                    )}
                    <span className="text-sm">{selectedTables.length} tables sélectionnées</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {autoBackupEnabled ? (
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    ) : (
                      <AlertTriangle className="h-4 w-4 text-yellow-500" />
                    )}
                    <span className="text-sm">
                      Sauvegarde automatique {autoBackupEnabled ? 'activée' : 'désactivée'}
                    </span>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Migration summary */}
              <div className="bg-muted p-4 rounded-lg space-y-2">
                <h4 className="font-medium">Résumé de la migration</h4>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• {selectedTables.length} tables à migrer</li>
                  {migrateStorage && <li>• {selectedBuckets.length} buckets Storage</li>}
                  <li>• Mode: Upsert (mise à jour ou insertion)</li>
                  {autoBackupEnabled && <li>• ✓ Sauvegarde automatique avant migration</li>}
                </ul>
              </div>

              <Button 
                onClick={() => setShowConfirmDialog(true)}
                disabled={!connectionStatus?.connected || !targetServiceKey || selectedTables.length === 0 || isMigrating || isBackingUp}
                className="w-full"
                size="lg"
              >
                {isBackingUp ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sauvegarde en cours...
                  </>
                ) : isMigrating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Migration en cours... {migrationProgress}%
                  </>
                ) : (
                  <>
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Démarrer la migration
                  </>
                )}
              </Button>

              {isMigrating && (
                <Progress value={migrationProgress} className="mt-2" />
              )}
            </CardContent>
          </Card>

          {/* Migration progress */}
          {migrationSteps.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Progression de la migration</CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {migrationSteps.map((step) => (
                      <div 
                        key={step.id}
                        className="flex items-center gap-3 p-3 rounded-lg border"
                      >
                        {step.status === 'pending' && (
                          <div className="h-5 w-5 rounded-full border-2 border-muted" />
                        )}
                        {step.status === 'in_progress' && (
                          <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
                        )}
                        {step.status === 'completed' && (
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        )}
                        {step.status === 'failed' && (
                          <XCircle className="h-5 w-5 text-red-500" />
                        )}
                        {step.status === 'skipped' && (
                          <AlertCircle className="h-5 w-5 text-yellow-500" />
                        )}
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium">{step.name}</p>
                            {step.rowsProcessed !== undefined && (
                              <span className="text-xs text-muted-foreground">
                                {step.rowsProcessed} / {step.totalRows}
                              </span>
                            )}
                          </div>
                          {step.message && (
                            <p className="text-xs text-muted-foreground truncate">
                              {step.message}
                            </p>
                          )}
                          {step.status === 'in_progress' && (
                            <Progress value={step.progress} className="h-1 mt-1" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la migration</DialogTitle>
            <DialogDescription>
              Vous êtes sur le point de migrer des données vers une nouvelle instance Supabase.
              Cette opération peut écraser des données existantes sur l'instance cible.
            </DialogDescription>
          </DialogHeader>
          
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <strong>Action irréversible.</strong> Assurez-vous d'avoir une sauvegarde de l'instance cible.
            </AlertDescription>
          </Alert>

          {autoBackupEnabled && (
            <Alert>
              <FolderArchive className="h-4 w-4" />
              <AlertTitle>Sauvegarde automatique</AlertTitle>
              <AlertDescription>
                Une sauvegarde complète sera téléchargée automatiquement avant le début de la migration.
              </AlertDescription>
            </Alert>
          )}

          <div className="bg-muted p-4 rounded-lg text-sm space-y-1">
            <p><strong>Cible:</strong> {connectionStatus?.projectId}</p>
            <p><strong>Tables:</strong> {selectedTables.length}</p>
            {migrateStorage && <p><strong>Buckets:</strong> {selectedBuckets.length}</p>}
            <p><strong>Sauvegarde auto:</strong> {autoBackupEnabled ? '✓ Activée' : '✗ Désactivée'}</p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={startMigration}>
              <Shield className="h-4 w-4 mr-2" />
              Confirmer la migration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
